import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useInView } from 'framer-motion';
import { ArrowUp, Check, ChevronDown, Mic, PanelLeft, Plus, Search, Share2, SlidersHorizontal, SquarePen } from 'lucide-react';
import { BrandLogo } from './AppChrome';
const GIGA_MARK = 'https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga-mark.png';

/**
 * The conversation, as a sequence of exchanges rather than swappable variants:
 * ChatGPT is a transcript, so the beats append downward and the thread scrolls
 * to follow — which is what makes it read as the real surface rather than a
 * card that re-renders in place.
 */
type Beat = {
  prompt: string;
  /** The connector call ChatGPT shows above an answer that used a tool. */
  call: string;
  detail: string;
  lead: string;
  body: React.ReactNode;
};
const AD_SETS = [{
  name: 'Ad set 1 · Broad',
  roas: '4.2×',
  cpa: '$18',
  good: true
}, {
  name: 'Ad set 2 · Lookalike',
  roas: '1.1×',
  cpa: '$72',
  good: false
}, {
  name: 'Ad set 3 · Interests',
  roas: '0.9×',
  cpa: '$88',
  good: false
}, {
  name: 'Ad set 4 · Retargeting',
  roas: '3.8×',
  cpa: '$21',
  good: true
}];
function AdSetTable() {
  return <div className="overflow-hidden rounded-lg border border-[#E5E5E5] text-[13px]" data-id="element-530" data-mp="3aaylh99wiy7f">
      <div className="grid grid-cols-[1.7fr_0.7fr_0.7fr] border-b border-[#E5E5E5] bg-[#F9F9F9] text-[11px] font-semibold text-[#5D5D5D]" data-id="element-531" data-mp="3a93zdrmw901r">
        <span className="px-3 py-2" data-id="element-532" data-mp="3j41v16hfeqws">Ad set</span>
        <span className="px-3 py-2 text-right" data-id="element-533" data-mp="3j5cz77esa0of">ROAS</span>
        <span className="px-3 py-2 text-right" data-id="element-534" data-mp="1u3t4nqqcw7l">CPA</span>
      </div>
      {AD_SETS.map(r => <div key={r.name} className="grid grid-cols-[1.7fr_0.7fr_0.7fr] border-b border-[#F0F0F0] last:border-0" data-id="element-535" data-mp="3t0wvhux9f1dp">
          <span className="truncate px-3 py-2 text-[#0D0D0D]" data-id="element-536" data-mp="23njkjmun6f3m">{r.name}</span>
          <span className={`px-3 py-2 text-right ${r.good ? 'text-[#0D0D0D]' : 'font-semibold text-[#C2410C]'}`} data-id="element-537" data-mp="1ynyl7bbk84qd">
            {r.roas}
          </span>
          <span className="px-3 py-2 text-right text-[#5D5D5D]" data-id="element-538" data-mp="2oddoa42xdawj">{r.cpa}</span>
        </div>)}
    </div>;
}
const BEATS: Beat[] = [{
  prompt: 'How did our Meta ads do last week?',
  call: 'Giga · Meta Ads',
  detail: 'Read 4 ad sets, last 7 days',
  lead: 'Here is last week across your four active ad sets.',
  body: <div className="flex flex-col gap-3" data-id="element-539" data-mp="329939yu7e1un">
      <AdSetTable data-id="element-540" data-mp="37b87faxrp2sn" />
      <p data-id="element-541" data-mp="379nc3j3mpftq">Ad sets 2 and 3 are well under your 2× ROAS target — worth a closer look.</p>
    </div>
}, {
  prompt: 'Which should we cut, and where should the budget go?',
  call: 'Giga · Company Brain',
  detail: 'Matched your ROAS and CPA targets',
  lead: 'Weighed against the targets in your growth track:',
  body: <ul className="flex list-disc flex-col gap-1 pl-5" data-id="element-542" data-mp="3l1bo7mdvo4ap">
      <li data-id="element-543" data-mp="3q1g5vfqiv52i">
        Pause <strong className="font-semibold" data-id="element-544" data-mp="3q1pnog1ey5x4">ad set 2</strong> — 1.1× ROAS, $72 CPA
      </li>
      <li data-id="element-545" data-mp="3uz7wm6ffkl54">
        Pause <strong className="font-semibold" data-id="element-546" data-mp="3v12flf75636k">ad set 3</strong> — 0.9× ROAS, $88 CPA
      </li>
      <li data-id="element-547" data-mp="1jdp8wld73bbp">Move that budget to ad sets 1 &amp; 4, your top performers</li>
    </ul>
}, {
  prompt: 'Go ahead — and let the team know in Slack.',
  call: 'Giga · Meta Ads, Slack',
  detail: '4 changes applied · posted to #growth',
  lead: 'Done. Changes are live in Meta Ads Manager:',
  body: <div className="flex flex-col gap-3" data-id="element-548" data-mp="14fwszs3qkkwz">
      <ul className="flex flex-col gap-1.5" data-id="element-549" data-mp="14h7xxv8x3ypo">
        {['Paused ad set 2', 'Paused ad set 3', 'Ad set 1 budget +40%', 'Ad set 4 budget +40%'].map(t => <li key={t} className="flex items-center gap-2" data-id="element-550" data-mp="1x4c96hylt99w">
            <Check size={14} strokeWidth={2.6} className="shrink-0 text-[#059669]" data-id="element-551" data-mp="1x4uavu41cfp7" />
            {t}
          </li>)}
      </ul>
      <blockquote className="border-l-2 border-[#E5E5E5] pl-3 text-[#5D5D5D]" data-id="element-552" data-mp="1ne961oxrqexu">
        Heads up team — pausing ad sets 2 &amp; 3 (ROAS under 1×) and moving budget to 1 &amp; 4.
      </blockquote>
    </div>
}];

/** The tool-call disclosure ChatGPT renders above an answer that used a connector. */
function ConnectorCall({
  call,
  detail,
  working
}: {
  call: string;
  detail: string;
  working: boolean;
}) {
  return <div className="inline-flex w-fit max-w-full items-center gap-2 rounded-lg border border-[#E5E5E5] bg-[#F9F9F9] px-2.5 py-1.5" data-id="element-553" data-mp="25kjbq8w28ep5">
      <img src={GIGA_MARK} alt="" aria-hidden="true" className="size-[15px] shrink-0 rounded-[4px]" data-id="element-554" data-mp="1qo0wmcku9akr" />
      <span className="truncate text-[12.5px] font-medium text-[#0D0D0D]" data-id="element-555" data-mp="1vmlrdwhbn21t">{call}</span>
      <span className="hidden truncate text-[12px] text-[#8F8F8F] sm:inline" data-id="element-556" data-mp="2jcg0q42fm8hb">
        {working ? 'Working…' : detail}
      </span>
      <ChevronDown size={13} className="shrink-0 text-[#B4B4B4]" data-id="element-557" data-mp="2o9q12erd04jx" />
    </div>;
}
const SIDEBAR_CHATS = ['Meta ads — last week', 'Q3 pipeline review', 'Renewal risk summary', 'Landing page copy'];

/**
 * ChatGPT's own chrome, rebuilt: collapsible sidebar with recents, the model
 * name as a dropdown in the header, an unbubbled assistant response, and the
 * pill composer. Previously this screen was a bare card holding two bubbles,
 * which is why it did not read as ChatGPT — the bubbles were right, but none of
 * the surrounding surface that identifies the product was there.
 */
export function ChatGPTPanel({
  className = '',
  // Shorter on a phone: a fixed 560px frame consumed nearly the whole visible
  // screen, so the surrounding page gave no context to what was being shown.
  heightClass = 'h-[440px] sm:h-[560px]'
}: {
  className?: string;
  heightClass?: string;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const threadRef = useRef<HTMLDivElement | null>(null);
  const inView = useInView(ref, {
    amount: 0.35
  });
  // How many exchanges have landed, and whether the newest is still working.
  const [shown, setShown] = useState(1);
  const [working, setWorking] = useState(false);
  useEffect(() => {
    if (!inView) return;
    setShown(1);
    setWorking(true);
  }, [inView]);
  useEffect(() => {
    if (!inView) return;
    if (working) {
      const id = setTimeout(() => setWorking(false), 1500);
      return () => clearTimeout(id);
    }
    if (shown >= BEATS.length) return;
    const id = setTimeout(() => {
      setShown(s => s + 1);
      setWorking(true);
    }, 2600);
    return () => clearTimeout(id);
  }, [inView, working, shown]);
  // Follow the transcript as it grows, the way the real thread does.
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTo({
      top: el.scrollHeight,
      behavior: 'smooth'
    });
  }, [shown, working]);
  return <div ref={ref} className={`overflow-hidden rounded-[20px] border border-[#E7E0D4] bg-white text-left shadow-[0_1px_2px_rgba(43,33,26,0.05),0_8px_24px_-12px_rgba(43,33,26,0.18),0_40px_100px_-40px_rgba(43,33,26,0.45)] ${className}`} data-id="element-558" data-mp="bytxt1diqefi">
      <div className={`flex ${heightClass}`} data-id="element-559" data-mp="ks718x97kyfu">
        {/* Sidebar */}
        {/* Only from md: at 640–767px a fixed 212px rail ate a third of the
         panel and squeezed the transcript it is meant to frame. */}
        <aside className="hidden w-[212px] shrink-0 flex-col bg-[#F9F9F9] px-2 py-3 md:flex" data-id="element-560" data-mp="3oentj32rw54o">
          <div className="mb-3 flex items-center justify-between px-1.5" data-id="element-561" data-mp="3gwfxxm2o3711">
            <BrandLogo slug="chatgpt" className="size-[18px]" data-id="element-562" data-mp="3byxvmej5nq20" />
            <PanelLeft size={15} className="text-[#B4B4B4]" data-id="element-563" data-mp="37w9ku1avs92e" />
          </div>
          {[{
          icon: SquarePen,
          label: 'New chat'
        }, {
          icon: Search,
          label: 'Search chats'
        }].map(item => <div key={item.label} className="flex items-center gap-2.5 rounded-lg px-2 py-[7px]" data-id="element-564" data-mp="2xydahbprpi5c">
              <item.icon size={15} strokeWidth={1.9} className="text-[#5D5D5D]" data-id="element-565" data-mp="2t750oekizurn" />
              <span className="text-[13.5px] text-[#0D0D0D]" data-id="element-566" data-mp="2o80s9q5fe12d">{item.label}</span>
            </div>)}
          <p className="mb-1 mt-4 px-2 text-[12px] font-medium text-[#8F8F8F]" data-id="element-567" data-mp="2k6yttbjc1t1g">Chats</p>
          <ul className="flex flex-col" data-id="element-568" data-mp="2f7m5emctcdwr">
            {SIDEBAR_CHATS.map((c, i) => <li key={c} className={`truncate rounded-lg px-2 py-[6px] text-[13px] ${i === 0 ? 'bg-[#EDEDED] font-medium text-[#0D0D0D]' : 'text-[#5D5D5D]'}`} data-id="element-569" data-mp="2f6ue3nq912ca" data-mpi={i}>
                {c}
              </li>)}
          </ul>
          <div className="mt-auto flex items-center gap-2.5 rounded-lg px-2 py-1.5" data-id="element-570" data-mp="1y04oxoojcim">
            <span className="flex size-[22px] items-center justify-center rounded-full bg-[#0D8B6C] text-[10px] font-semibold text-white" data-id="element-571" data-mp="6wtjctfpd9kd">
              SC
            </span>
            <span className="truncate text-[13px] text-[#0D0D0D]" data-id="element-572" data-mp="3u2anj6pall6o">Sarah Chen</span>
          </div>
        </aside>

        {/* Conversation */}
        <div className="flex min-w-0 flex-1 flex-col bg-white" data-id="element-573" data-mp="3k5fsixs8a2x5">
          <header className="flex shrink-0 items-center justify-between px-4 py-3" data-id="element-574" data-mp="3k70momamrjuh">
            <span className="inline-flex items-center gap-1 text-[15px] font-medium text-[#0D0D0D]" data-id="element-575" data-mp="36fcbqmc4b1f3">
              ChatGPT
              <span className="text-[#8F8F8F]" data-id="element-576" data-mp="3afw8hoss430p">5</span>
              <ChevronDown size={15} className="text-[#8F8F8F]" data-id="element-577" data-mp="2wflfog7wsecw" />
            </span>
            <span className="flex items-center gap-3 text-[#5D5D5D]" data-id="element-578" data-mp="31eokbzjz8ikc">
              <Share2 size={16} strokeWidth={1.9} data-id="element-579" data-mp="31gsp0jpp9llo" />
              <SlidersHorizontal size={16} strokeWidth={1.9} data-id="element-580" data-mp="2hu7n2n2efp1u" />
            </span>
          </header>

          <div ref={threadRef} className="flex-1 overflow-hidden px-4 pb-2 sm:px-6" data-id="element-581" data-mp="2cv4qzs4ect60">
            <div className="mx-auto flex max-w-[620px] flex-col gap-6 py-2 text-[15px] leading-[1.65] text-[#0D0D0D]" data-id="element-582" data-mp="2rrm3imobodu5">
              {BEATS.slice(0, shown).map((beat, i) => {
              const isLatest = i === shown - 1;
              const pending = isLatest && working;
              return <div key={beat.prompt} className="flex flex-col gap-4" data-id="element-583" data-mp="2vt79rp6cptvd" data-mpi={i}>
                    <motion.div initial={{
                  opacity: 0,
                  y: 8
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  duration: 0.22,
                  ease: [0.23, 1, 0.32, 1]
                }} className="max-w-[85%] self-end rounded-[22px] bg-[#F4F4F4] px-4 py-2.5" data-id="element-584" data-mp="2vr39ribkpf0d">
                      {beat.prompt}
                    </motion.div>
                    <div className="flex flex-col gap-3" data-id="element-585" data-mp="3hjrc083saaev">
                      <ConnectorCall call={beat.call} detail={beat.detail} working={pending} data-id="element-586" data-mp="3crzj3qx5nslk" />
                      <AnimatePresence mode="wait" data-id="element-587" data-mp="3cq6eeh63rig7">
                        {pending ? <motion.div key="working" initial={{
                      opacity: 0
                    }} animate={{
                      opacity: 1
                    }} exit={{
                      opacity: 0
                    }} transition={{
                      duration: 0.15
                    }} className="flex items-center gap-1.5 py-1" data-id="element-588" data-mp="37tfv7kz046qk">
                            {[0, 1, 2].map(d => <motion.span key={d} className="size-[6px] rounded-full bg-[#B4B4B4]" animate={{
                        opacity: [0.3, 1, 0.3]
                      }} transition={{
                        duration: 1.1,
                        repeat: Infinity,
                        delay: d * 0.16,
                        ease: 'linear'
                      }} data-id="element-589" data-mp="3qi2zi19css33" />)}
                          </motion.div> : <motion.div key="answer" initial={{
                      opacity: 0
                    }} animate={{
                      opacity: 1
                    }} transition={{
                      duration: 0.22
                    }} className="flex flex-col gap-3" data-id="element-590" data-mp="qlwbuk8bgxoc">
                            <p data-id="element-591" data-mp="14d1elv6ze62w">{beat.lead}</p>
                            {beat.body}
                          </motion.div>}
                      </AnimatePresence>
                    </div>
                  </div>;
            })}
            </div>
          </div>

          {/* Composer */}
          <div className="shrink-0 px-4 pb-4 sm:px-6" data-id="element-592" data-mp="1ibj0dndbt8i6">
            <div className="mx-auto flex max-w-[620px] items-center gap-2 rounded-[26px] border border-[#E5E5E5] bg-white px-3 py-2.5 shadow-[0_2px_8px_rgba(13,13,13,0.05)]" data-id="element-593" data-mp="1i9oheelm7qgq">
              <Plus size={18} strokeWidth={1.9} className="shrink-0 text-[#5D5D5D]" data-id="element-594" data-mp="3k4p27003um91" />
              <span className="min-w-0 flex-1 truncate text-[15px] text-[#8F8F8F]" data-id="element-595" data-mp="3p309b2z0mink">Ask anything</span>
              <Mic size={17} strokeWidth={1.9} className="shrink-0 text-[#5D5D5D]" data-id="element-596" data-mp="w8jngb1tgnw5" />
              <span className="flex size-[28px] shrink-0 items-center justify-center rounded-full bg-[#0D0D0D]" data-id="element-597" data-mp="r9p5fbbob3d3">
                <ArrowUp size={15} strokeWidth={2.4} className="text-white" data-id="element-598" data-mp="1597zj07nge6e" />
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>;
}